import { UserNeedDto } from '@act/shared/data-transfer-objects';
import {
  EntityAdapter,
  PayloadAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { needsRequests } from '../requests/needs.requests';
import { useAppSelector } from '../store-hooks';
import { NeedsState } from '../types/needsTypes';
import { createApiThunk } from '../utils/api-thunk';
import { selectMember } from './selectedMemberSlice';
import { resetRedux } from '../actions/shared-actions';

// THUNKS/ACTIONS ==============================================================
export const updateUserNeedsOrder = createApiThunk(
  needsRequests.updateUserNeedOrder
);
export const getUserNeeds = createApiThunk(needsRequests.getUserNeeds);
export const getNeedCategories = createApiThunk(
  needsRequests.getNeedCategories
);
export const createUserNeed = createApiThunk(needsRequests.postUserNeed);
export const patchUserNeed = createApiThunk(needsRequests.patchUserNeed);
export const deleteUserNeed = createApiThunk(needsRequests.deleteUserNeed);

// INITIAL STATE ===============================================================
const needsAdapter: EntityAdapter<UserNeedDto, number> =
  createEntityAdapter<UserNeedDto>({
    sortComparer: (a, b) => a.sortOrder - b.sortOrder,
  });

const initialState: NeedsState = needsAdapter.getInitialState({
  editedUserNeedId: null,
  selectedMemberId: null,
  status: 'initial',
  inflightCreationIds: [],
  needCategories: [],
});

// SLICE =======================================================================
export const needsSlice = createSlice({
  name: 'needs',
  initialState: initialState,
  reducers: {
    userNeedUpdatedCreated: (
      state: NeedsState,
      action: PayloadAction<UserNeedDto>
    ) => {
      needsAdapter.upsertOne(state, action.payload);
      state.status = 'success';
    },
    userNeedDeleted: (state: NeedsState, action: PayloadAction<number>) => {
      needsAdapter.removeOne(state, action.payload);
      state.status = 'success';
    },
    userNeedLoading: (state: NeedsState) => {
      state.status = 'loading';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(getUserNeeds.pending, state => {
        state = needsAdapter.removeAll(state);
        state.status = 'loading';
      })
      .addCase(getUserNeeds.fulfilled, (state, action) => {
        state = needsAdapter.upsertMany(state, action.payload);
        state.status = 'success';
      })
      .addCase(getUserNeeds.rejected, state => {
        state = needsAdapter.removeAll(state);
        state.status = 'failed';
      })

      // Reorder Request
      .addCase(updateUserNeedsOrder.pending, state => {
        state.status = 'loading';
      })
      .addCase(updateUserNeedsOrder.fulfilled, (state, action) => {
        needsAdapter.upsertMany(state, action.payload);
        state.status = 'success';
      })
      .addCase(updateUserNeedsOrder.rejected, state => {
        state.status = 'failed';
      })

      // CATEGORY Request
      .addCase(getNeedCategories.pending, state => {
        state.status = 'loading';
      })
      .addCase(getNeedCategories.fulfilled, (state, action) => {
        state.needCategories = action.payload;
        state.status = 'success';
      })
      .addCase(getNeedCategories.rejected, state => {
        state = needsAdapter.removeAll(state);
        state.status = 'failed';
      })

      .addCase(selectMember, (state, action) => {
        state = needsAdapter.removeAll(state);
        state.status = 'initial';
        state.selectedMemberId = action.payload;
      })

      .addCase(createUserNeed.pending, state => {
        state.status = 'loading';
      })
      .addCase(
        createUserNeed.fulfilled,
        (state, action: PayloadAction<UserNeedDto>) => {
          state = needsAdapter.upsertOne(state, action.payload);
          state.status = 'success';
        }
      )
      .addCase(createUserNeed.rejected, state => {
        state.status = 'failed';
      })

      .addCase(patchUserNeed.pending, state => {
        state.status = 'loading';
      })
      .addCase(
        patchUserNeed.fulfilled,
        (state, action: PayloadAction<UserNeedDto>) => {
          state = needsAdapter.upsertOne(state, action.payload);
          state.status = 'success';
        }
      )
      .addCase(patchUserNeed.rejected, state => {
        state.status = 'failed';
      })

      // Delete User Need ======================================================
      .addCase(deleteUserNeed.fulfilled, (state, action) => {
        state = needsAdapter.removeOne(state, action.payload.id);
        state.status = 'success';
      });
  },
});

// REDUCER =====================================================================
export const needsReducer = needsSlice.reducer;

// SELECTORS ===================================================================
const selectNeedsState = state =>
  (state as { needs: typeof initialState })?.needs;

export const selectNeedsCategories = createSelector(
  selectNeedsState,
  state => state.needCategories
);
export const selectUserNeeds = createSelector(selectNeedsState, state => {
  const entities = [];
  state.ids.forEach(id => entities.push(state.entities[id]));
  return entities;
});

const selectSliceStatus = createSelector(
  selectNeedsState,
  needsState => needsState.status
);

// EXPORT HELPER ===============================================================
export const needsAppSelectors = {
  useSelectNeedsCategories: () => useAppSelector(selectNeedsCategories),
  useSelectSliceStatus: () => useAppSelector(selectSliceStatus),
};

// EFFECTS =====================================================================
// WEBSOCKET ACTIONS ===========================================================
